import React, { MouseEvent, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Window, WindowActionsBar, WindowMoveEvent, WindowActionsEvent } from '@progress/kendo-react-dialogs';
import { windowIcon } from '@progress/kendo-svg-icons';
import cn from 'clsx';
import Button from '@components-clean/core/button';
import { DraggableDialogProps } from '@components-clean/core/draggable-dialog/draggable-dialog.interface';
import { ReadOnlyContextProvider, useReadOnlyContext } from '@contexts-clean/read-only-context';
import { useTranslation, useWindowSize } from '@hooks/index';
import { NullableComponentRef, setAttribute } from '@utils/automation-test-helpers';

import './index.scss';

const MinimizedDialogHeight = 32;

interface DialogBounds {
    left: number;
    top: number;
    width: number;
    height: number;
}

const RestoreButton = ({ onClick }: { onClick: (e: MouseEvent<HTMLButtonElement>) => void }) => (
    <Button
        data-testid="dialog-restore-button"
        svgIcon={windowIcon}
        fillMode="flat"
        onClick={(e) => {
            if (onClick) {
                onClick(e);
            }
        }}
    />
);

const DraggableDialog = ({
    dataTestId,
    title,
    primaryBtnLabel,
    primaryBtnDisabled,
    secondaryBtnLabel,
    secondaryBtnDisabled,
    onClose,
    onSubmit,
    children,
    className,
    createdUpdatedInfo,
    initialWidth,
    initialHeight,
    draggable,
    modal,
    setReadOnlyContext = true,
    minWidth,
    additionalInfoBtnLabel,
    additionalInfo,
    additionalInfoClasses,
    additionalInfoWidth,
    hideTitleCloseButton,
    closeButton,
    bodyPadding = 'default',
    createdUpdatedBackgroundColour = 'white',
    titleBarStyle = 'default',
    style,
    onAdditionalAction,
    additionalBtnLabel,
    additionalBtnDisabled,
    stepCount,
    currentStep,
    onStepAction,
    currentStepTitle,
    nextBtnDisabled,
    ...rest
}: DraggableDialogProps) => {
    const { t } = useTranslation();
    const { setReadOnly } = useReadOnlyContext();

    const windowDimensions = useWindowSize();

    const ref = useRef<NullableComponentRef>(null);

    const [dialogWidth, setDialogWidth] = useState<number>(initialWidth ?? 450);
    const [dialogHeight] = useState<number>(initialHeight ?? 250);
    const additionalInfoDialogWidth = additionalInfoWidth ?? 300;

    const [position, setPosition] = useState<DialogBounds>({
        left: windowDimensions.width / 2 - dialogWidth / 2,
        top: windowDimensions.height / 2 - dialogHeight / 2,
        width: dialogWidth,
        height: dialogHeight,
    });
    const [windowStage, setWindowStage] = useState<'MINIMIZED' | 'FULLSCREEN' | 'DEFAULT' | undefined>('DEFAULT');

    const [open, setOpen] = useState(false);

    useEffect(() => {
        // What if it's been moved, should it reset????
        if (windowStage === 'DEFAULT') {
            setPosition({
                ...position,
                left: windowDimensions.width / 2 - dialogWidth / 2,
                top: windowDimensions.height / 2 - dialogHeight / 2,
            });
        }
    }, [windowDimensions]);

    const handleMove = (event: WindowMoveEvent) => {
        if (!windowDimensions) {
            return;
        }

        const minTop = 0;
        const minLeft = 0;
        const maxTop = windowDimensions.height - (event.height || MinimizedDialogHeight);
        const maxLeft = windowDimensions.width - (open ? event.width + additionalInfoDialogWidth : event.width);

        if (event.top < minTop) {
            event.top = minTop;
        }

        if (event.left < minLeft) {
            event.left = minLeft;
        }

        if (event.top > maxTop) {
            event.top = maxTop;
        }

        if (event.left > maxLeft) {
            event.left = maxLeft;
        }
        setPosition({ ...position, left: event.left, top: event.top });
    };

    /* NOTE: This function updates the dialog position to make it fully visible
        when you move the minimized dialog to the bottom of the screen and then expand it */
    const updatePosOnExpand = (state: typeof windowStage) => {
        if (state === 'DEFAULT') {
            const maxTop = windowDimensions.height - dialogHeight;
            if (maxTop < position.top) {
                setPosition({ ...position, top: maxTop });
            }
        }
    };

    const onStageChange = (ev: WindowActionsEvent) => {
        updatePosOnExpand(ev.state);
        setWindowStage(ev.state);
    };

    useEffect(() => setAttribute(ref, 'data-testid', dataTestId));

    useEffect(() => {
        const nav = document.getElementById('main-nav');
        const header = document.getElementById('main-header');
        if (nav) {
            nav.style.pointerEvents = 'none';
        }
        if (header) {
            header.style.pointerEvents = 'none';
        }

        if (setReadOnlyContext) setReadOnly(true);

        return () => {
            if (nav) {
                nav.style.pointerEvents = 'auto';
            }
            if (header) {
                header.style.pointerEvents = 'auto';
            }

            setReadOnly(false);
        };
    }, []);

    useEffect(() => {
        if (!additionalInfo && open) {
            setOpen(false);
            setDialogWidth((prev) => prev - additionalInfoDialogWidth);
        }
    }, [additionalInfo]);

    const handleButtonClick = () => {
        const show = !open;
        setOpen(show);
        setDialogWidth(show ? dialogWidth + additionalInfoDialogWidth : dialogWidth - additionalInfoDialogWidth);
    };

    const isCompletionStep = stepCount != null && currentStep != null && currentStep > stepCount;

    const stepIndicator =
        stepCount && currentStep != null ? (
            <div className="step-indicator">
                {Array.from({ length: stepCount }, (_, i) => {
                    const num = i + 1;
                    return (
                        <React.Fragment key={num}>
                            {i > 0 && <div className="step-connector" />}
                            <span className={cn('step-badge', { 'step-badge--active': num === currentStep })}>
                                {num}
                            </span>
                        </React.Fragment>
                    );
                })}
                <div className="step-connector" />
                <span className={cn('step-badge', { 'step-badge--active': isCompletionStep })}>
                    <FontAwesomeIcon icon={['far', 'check']} />
                </span>
            </div>
        ) : null;

    const bodyContent = () => {
        if (!open) {
            if (stepCount) {
                return (
                    <div className="body-content k-d-flex k-flex-col k-h-full" style={style ?? { minHeight: 65 }}>
                        {stepIndicator}
                        <div className="step-content">
                            {isCompletionStep ? (
                                <div className="k-d-flex k-align-items-center k-justify-content-center k-h-full">
                                    <FontAwesomeIcon icon={['far', 'check']} className="step-completion-check" />
                                </div>
                            ) : (
                                children
                            )}
                        </div>
                    </div>
                );
            }
            return (
                <div
                    className={cn('body-content k-d-flex k-align-items-center k-h-1 k-justify-content-center k-h-full')}
                    style={style ?? { minHeight: 65 }}
                >
                    {children}
                </div>
            );
        }

        return (
            <div className={cn('body-content k-d-flex k-h-full')}>
                <div
                    className={cn(
                        'k-h-full',
                        stepCount ? 'k-d-flex k-flex-col' : 'k-d-flex k-align-items-center k-justify-content-center',
                    )}
                    style={{ minHeight: 65, width: `calc(100% - ${additionalInfoDialogWidth}px)` }}
                >
                    {stepCount ? (
                        <>
                            {stepIndicator}
                            <div className="step-content">
                                {isCompletionStep ? (
                                    <div className="k-d-flex k-align-items-center k-justify-content-center k-h-full">
                                        <FontAwesomeIcon icon={['far', 'check']} className="step-completion-check" />
                                    </div>
                                ) : (
                                    children
                                )}
                            </div>
                        </>
                    ) : (
                        children
                    )}
                </div>
                <div
                    className={cn('k-h-full', additionalInfoClasses)}
                    style={{ width: `${additionalInfoDialogWidth}px` }}
                >
                    {additionalInfo}
                </div>
            </div>
        );
    };

    return (
        <ReadOnlyContextProvider>
            <Window
                title={
                    <>
                        {(draggable || draggable === undefined) && (
                            <FontAwesomeIcon
                                icon={['fas', 'grip-dots-vertical']}
                                className={cn('k-mr-1.5 k-opacity-50')}
                            />
                        )}
                        {title}
                        {currentStepTitle && (
                            <span className="currentStepTitle">
                                ({currentStepTitle} - {t('common:step')} {currentStep}/{stepCount})
                            </span>
                        )}
                        {additionalInfo && (
                            <Button
                                className="k-ml-auto k-h-5 k-mr-0.5"
                                data-testid="dialog-additional-info-button"
                                alternativeStyle
                                primary
                                faIcon={['fas', 'arrow-right-long-to-line']}
                                faIconPosition="end"
                                faIconStyle={open ? { transform: 'rotate(180deg)' } : undefined}
                                onClick={handleButtonClick}
                            >
                                {`${open ? t('common:hide') : t('common:show')} ${additionalInfoBtnLabel}`}
                            </Button>
                        )}
                    </>
                }
                onClose={onClose}
                modal={modal ?? false}
                overlayStyle={{ backgroundColor: 'transparent' }}
                ref={ref}
                initialWidth={dialogWidth}
                width={dialogWidth}
                initialHeight={dialogHeight}
                restoreButton={RestoreButton}
                maximizeButton={() => null}
                doubleClickStageChange={false}
                resizable={false}
                draggable={draggable}
                left={position.left}
                top={position.top}
                closeButton={hideTitleCloseButton ? () => null : closeButton}
                onMove={handleMove}
                stage={windowStage}
                onStageChange={onStageChange}
                className={cn(
                    'draggable-dialog !k-pos-fixed k-user-select-none',
                    `created-updated-info-${createdUpdatedBackgroundColour}`,
                    className,
                    {
                        'padding-none': bodyPadding === 'none',
                        'alternate-titlebar': titleBarStyle === 'alternate',
                    },
                )}
                {...rest}
            >
                {bodyContent()}
                {createdUpdatedInfo && <WindowActionsBar layout="start">{createdUpdatedInfo}</WindowActionsBar>}
                {onClose && isCompletionStep && (
                    <WindowActionsBar layout="end">
                        <Button
                            className="k-mr-1"
                            onClick={() => onClose?.({ action: 'button' })}
                            data-testid={`${dataTestId}-close-button`}
                            disableFocus
                        >
                            {t('common:close')}
                        </Button>
                    </WindowActionsBar>
                )}
                {(onClose || onSubmit || onAdditionalAction) && stepCount && !isCompletionStep && (
                    <WindowActionsBar layout="start">
                        <Button
                            className="k-ml-1"
                            onClick={() => onStepAction?.({ action: 'back' })}
                            data-testid={`${dataTestId}-back-button`}
                            disableFocus
                            disabled={currentStep === 1}
                            faIcon={['far', 'arrow-left']}
                            faIconPosition="start"
                            faIconStyle={{ scale: '0.75' }}
                        >
                            {t('common:back')}
                        </Button>
                        <div className="k-ml-auto">
                            {onClose && (
                                <Button
                                    className="k-mr-0.5"
                                    onClick={() => onClose?.({ action: 'button' })}
                                    data-testid={`${dataTestId}-cancel-button`}
                                    disableFocus
                                    disabled={secondaryBtnDisabled}
                                >
                                    {secondaryBtnLabel ?? t('cancel')}
                                </Button>
                            )}
                            {onSubmit && currentStep === stepCount && (
                                <Button
                                    className="k-mr-1"
                                    onClick={onSubmit}
                                    data-testid={`${dataTestId}-save-submit`}
                                    primary
                                    disabled={primaryBtnDisabled}
                                >
                                    {primaryBtnLabel ?? t('confirm')}
                                </Button>
                            )}
                            {onAdditionalAction && currentStep === stepCount && (
                                <div className="k-pl-0.5">
                                    <Button
                                        onClick={onAdditionalAction}
                                        data-testid={`${dataTestId}-additional-save`}
                                        primary
                                        disabled={additionalBtnDisabled}
                                        alternativeStyle
                                    >
                                        {additionalBtnLabel ?? t('confirm')}
                                    </Button>
                                </div>
                            )}
                            {currentStep !== stepCount && (
                                <Button
                                    className="k-mr-1"
                                    onClick={() => onStepAction?.({ action: 'next' })}
                                    data-testid={`${dataTestId}-next-button`}
                                    primary
                                    disabled={nextBtnDisabled}
                                    faIcon={['far', 'arrow-right']}
                                    faIconPosition="end"
                                    faIconStyle={{ scale: '0.75' }}
                                >
                                    {t('common:next')}
                                </Button>
                            )}
                        </div>
                    </WindowActionsBar>
                )}
                {(onClose || onSubmit || onAdditionalAction) && !stepCount && (
                    <WindowActionsBar layout="end">
                        {onClose && (
                            <Button
                                className="k-mr-0.5"
                                onClick={() => onClose?.({ action: 'button' })}
                                data-testid={`${dataTestId}-cancel-button`}
                                disableFocus
                                disabled={secondaryBtnDisabled}
                            >
                                {secondaryBtnLabel ?? t('cancel')}
                            </Button>
                        )}
                        {onSubmit && (
                            <Button
                                onClick={onSubmit}
                                data-testid={`${dataTestId}-save-submit`}
                                primary
                                disabled={primaryBtnDisabled}
                            >
                                {primaryBtnLabel ?? t('confirm')}
                            </Button>
                        )}
                        {onAdditionalAction && (
                            <div className="k-pl-0.5">
                                <Button
                                    onClick={onAdditionalAction}
                                    data-testid={`${dataTestId}-additional-save`}
                                    primary
                                    disabled={additionalBtnDisabled}
                                    alternativeStyle
                                >
                                    {additionalBtnLabel ?? t('confirm')}
                                </Button>
                            </div>
                        )}
                    </WindowActionsBar>
                )}
            </Window>
        </ReadOnlyContextProvider>
    );
};

export default DraggableDialog;
